import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Print } from '@mui/icons-material';
import { useRouter } from '../contexts/RouterContext';
import { useAuth } from '../contexts/AuthContext';
import { patientFinancialService, PaymentReceipt } from '../services/patientFinancialService';
import { printTemplateService, renderTemplateHtml, PrintTemplate } from '../services/printTemplateService';
import { PrintTemplateBar, usePrintTemplateChoice } from './print/PrintTemplateChoice';
import { colors } from '../theme/designSystem';

/**
 * Recibo de pagamento do paciente (?page=payment-receipt&id=<pagamento>): cabeçalho e rodapé do
 * TEMPLATE PADRÃO da entidade, itens pagos discriminados (sem a presença — decisão do Hilton, 05/09),
 * formas de pagamento, pagador e emissor. Identificação do recibo = data + id.
 */
const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDate = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const fmtDateTime = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fmtDate(iso);
  const date = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${date} ${time}`;
};

const PaymentReceiptPrint: React.FC = () => {
  const { getParam } = useRouter();
  const { user } = useAuth();
  const paymentId = getParam('id') || '';
  const autoPrint = getParam('print') === '1';
  const [receipt, setReceipt] = useState<PaymentReceipt | null>(null);
  const [templates, setTemplates] = useState<PrintTemplate[]>([]);
  const templateChoice = usePrintTemplateChoice(templates, 'payment-receipt');
  const template = templateChoice.template;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Clinic4Us - Recibo';
    const load = async () => {
      try {
        const [data, templates] = await Promise.all([
          patientFinancialService.getReceipt(paymentId),
          user?.entityId ? printTemplateService.getByEntity(user.entityId).catch(() => []) : Promise.resolve([]),
        ]);
        setReceipt(data);
        setTemplates(templates);
      } catch {
        setError('Não foi possível carregar o recibo.');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentId]);

  useEffect(() => {
    if (autoPrint && receipt && !loading) {
      const t = window.setTimeout(() => window.print(), 600);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [autoPrint, receipt, loading]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: colors.primary }} />
      </Box>
    );
  }

  if (error || !receipt) {
    return (
      <Typography sx={{ color: colors.error, textAlign: 'center', py: 8 }}>
        {error || 'Recibo não encontrado.'}
      </Typography>
    );
  }

  const programmedTotal = receipt.items.reduce((t, i) => t + i.programmedDiscount, 0);
  const paymentAdjustTotal = receipt.items.reduce((t, i) => t + i.discount, 0);
  const shortId = receipt.paymentId.slice(0, 8).toUpperCase();
  const cell: React.CSSProperties = { padding: '1.5mm 1.5mm', borderBottom: '1px solid #ddd', fontSize: '9.5pt', verticalAlign: 'top' };
  const right: React.CSSProperties = { ...cell, textAlign: 'right', whiteSpace: 'nowrap' };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', padding: '2rem', '@media print': { padding: 0, backgroundColor: '#fff' } }}>
      <style>{`
        @media print {
          /* Margem da folha zerada: sem ela o navegador não imprime data, título da aba, URL e número de
             página. Os 12mm viram padding da própria folha (296mm, não 297, para não sobrar folha em branco). */
          @page { size: A4 portrait; margin: 0; }
          .receipt-print-page { padding: 12mm !important; width: auto !important; min-height: 296mm !important; box-shadow: none !important; margin: 0 !important; }
          .print-frame-header img { max-height: 18mm !important; }
        }
      `}</style>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, maxWidth: '210mm', mx: 'auto', '@media print': { display: 'none' } }}>
        <PrintTemplateBar choice={templateChoice} />
        <Button variant="contained" startIcon={<Print />} onClick={() => window.print()}
          sx={{ backgroundColor: colors.primary, textTransform: 'none', '&:hover': { backgroundColor: '#029AAB' } }}>
          Imprimir
        </Button>
      </Box>

      <Box className="receipt-print-page" sx={{
        width: '210mm', maxWidth: '100%', minHeight: '297mm', padding: '15mm', boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
        background: 'white', margin: '0 auto 2rem', position: 'relative', boxShadow: '0 0 8px rgba(0,0,0,0.15)', fontSize: '12pt', color: '#000',
      }}>
        {template && (
<div className="print-frame-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10mm' }}>
            <div style={{ flexShrink: 0 }}>
              {template?.file ? <img src={template.file} alt="logo" style={{ maxHeight: '30mm', maxWidth: '70mm', objectFit: 'contain' }} /> : null}
            </div>
            <div style={{ textAlign: 'right', fontSize: '10pt', lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: renderTemplateHtml(template?.header) }} />
          </div>
          <hr style={{ border: 'none', borderTop: '1px solid #999', margin: '6mm 0' }} />
        </div>
)}

        <Box sx={{ flex: '1 1 auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: '6mm' }}>
            <Typography sx={{ fontSize: '16pt', fontWeight: 700, color: '#212529', letterSpacing: '.04em' }}>RECIBO{receipt.reverted ? ' — ESTORNADO' : ''}</Typography>
            <Typography sx={{ fontSize: '10pt', color: '#6c757d' }}>{fmtDateTime(receipt.date)} · Nº {shortId}</Typography>
          </Box>

          <Box sx={{ mb: '6mm', fontSize: '11pt', color: '#212529', lineHeight: 1.6 }}>
            <div><strong>Paciente:</strong> {receipt.patientName}</div>
            {receipt.payerName && (
              <div><strong>Pagador:</strong> {receipt.payerName}{receipt.payerDocument ? ` · ${receipt.payerDocument}` : ''}</div>
            )}
          </Box>

          <Typography sx={{ fontSize: '11pt', mb: '4mm', color: '#212529' }}>
            Recebemos a importância de <strong>{brl(receipt.total)}</strong> referente aos atendimentos abaixo{receipt.byInjunction ? ', pagos por liminar judicial' : ''}.
          </Typography>

          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '6mm' }}>
            <thead>
              <tr style={{ background: '#f1f3f5' }}>
                <th style={{ ...cell, textAlign: 'left', fontWeight: 600 }}>Data</th>
                <th style={{ ...cell, textAlign: 'left', fontWeight: 600 }}>Serviço</th>
                <th style={{ ...right, fontWeight: 600 }}>Tabela</th>
                <th style={{ ...right, fontWeight: 600 }}>Desconto</th>
                <th style={{ ...right, fontWeight: 600 }}>Ajuste</th>
                <th style={{ ...right, fontWeight: 600 }}>Pago</th>
              </tr>
            </thead>
            <tbody>
              {receipt.items.map((it) => (
                <tr key={it.scheduleId}>
                  <td style={{ ...cell, whiteSpace: 'nowrap' }}>{fmtDateTime(it.date)}</td>
                  <td style={cell}>{[it.serviceTypeName, it.professionalTypeName].filter(Boolean).join(' - ') || '—'}</td>
                  <td style={right}>{brl(it.unitValue)}</td>
                  <td style={right}>{it.programmedDiscount ? `- ${brl(it.programmedDiscount)}` : '—'}</td>
                  <td style={right}>{it.discount ? `${it.discount > 0 ? '- ' : '+ '}${brl(Math.abs(it.discount))}` : '—'}</td>
                  <td style={{ ...right, fontWeight: 600 }}>{brl(it.finalValue)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2} style={{ ...cell, borderBottom: 'none' }} />
                <td style={{ ...right, borderBottom: 'none', color: '#6c757d' }}>{brl(receipt.originalTotal)}</td>
                <td style={{ ...right, borderBottom: 'none', color: '#6c757d' }}>{programmedTotal ? `- ${brl(programmedTotal)}` : '—'}</td>
                <td style={{ ...right, borderBottom: 'none', color: '#6c757d' }}>{paymentAdjustTotal ? `${paymentAdjustTotal > 0 ? '- ' : '+ '}${brl(Math.abs(paymentAdjustTotal))}` : '—'}</td>
                <td style={{ ...right, borderBottom: 'none', fontWeight: 700, fontSize: '11pt' }}>{brl(receipt.total)}</td>
              </tr>
            </tfoot>
          </table>

          {/* Forma de pagamento saiu do recibo (Hilton, 07/09/2026): continua no caixa e nos Recebimentos */}
          {receipt.comments && (
            <Box sx={{ mb: '4mm', fontSize: '10.5pt', color: '#495057' }}><strong>Observações:</strong> {receipt.comments}</Box>
          )}
          {receipt.invoiceUrl && (
            <Box sx={{ mb: '4mm', fontSize: '10.5pt', color: '#495057' }}><strong>Nota fiscal:</strong> <a href={receipt.invoiceUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#0d6e6e' }}>{receipt.invoiceUrl}</a></Box>
          )}

          {/* Só a linha da assinatura (Hilton, 27/09/2026); a data já está no cabeçalho */}
          <Box sx={{ mt: '18mm', textAlign: 'center' }}>
            <div style={{ borderTop: '1px solid #000', width: '80mm', margin: '0 auto' }} />
          </Box>
        </Box>

        {template && (
<div style={{ marginTop: 'auto', textAlign: 'center', fontSize: '9pt', paddingTop: '6mm', borderTop: '1px solid #ddd' }} dangerouslySetInnerHTML={{ __html: renderTemplateHtml(template?.footer) }} />
)}
      </Box>
    </Box>
  );
};

export default PaymentReceiptPrint;
